import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';

/**
 * Standard dialog shell — header (title + close-X), scrollable body slot,
 * sticky footer slot. Owns padding, responsive breakpoints and scroll behavior
 * so feature dialogs only deal with their own content.
 *
 * Usage:
 *   <sms-dialog [title]="'Edit Student'" [size]="'md'" (close)="dialogRef.close()">
 *     <ng-container body>
 *       ...form / content...
 *     </ng-container>
 *     <ng-container footer>
 *       <button class="secondary-btn" (click)="dialogRef.close()">Cancel</button>
 *       <button class="primary-button" (click)="save()">Save</button>
 *     </ng-container>
 *   </sms-dialog>
 *
 * The dialog opener decides the OUTER size via panelClass:
 *   this.dialog.open(MyDialog, { panelClass: 'sms-dialog-md' });
 *
 * Sizes (see _dialog.scss): sms-dialog-sm = 400px, -md = 600px, -lg = 850px,
 * -xl = 1100px. All collapse to full-viewport at <=768px.
 */
@Component({
  selector: 'sms-dialog',
  templateUrl: './sms-dialog.component.html',
  styleUrls: ['./sms-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SmsDialogComponent {
  /** Title shown on the left of the header. Required. */
  @Input() title = '';

  /** Optional secondary line under the title (e.g. record name / context). */
  @Input() subtitle: string | null = null;

  /** Size hint — only used when the dialog opener forgot to set panelClass. */
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';

  /** Hide the close-X (rare — confirm dialogs that demand a button choice). */
  @Input() showClose = true;

  /** Show a destructive accent strip in the header (red 4px top border). */
  @Input() danger = false;

  /**
   * Header colour theme.
   *  - 'teal'    (default) light-teal background, dark title — subtle lane
   *  - 'inverse' solid teal background, white title — bolder branded look
   */
  @Input() headerStyle: 'teal' | 'inverse' = 'teal';

  /** Emitted when user clicks the close-X. Parent should call dialogRef.close(). */
  @Output() close = new EventEmitter<void>();

  onClose(): void {
    this.close.emit();
  }
}
